/**
 * Confirmation page - shown after entry submission
 */
import { BaseElement } from '../base-element';
import { Signal } from 'signal-polyfill';
import { entriesApi } from '../../api/entries.api';
import { authState } from '../../state/auth.state';
import { navigate } from '../../router';
import type { EntryDetailDto } from '../../api/types';

type PageState = 'loading' | 'loaded' | 'error';

export class ConfirmationPage extends BaseElement {
  private entry = new Signal.State<EntryDetailDto | null>(null);
  private loading = new Signal.State(true);
  private errorMessage = new Signal.State<string | null>(null);
  private supportId = new Signal.State<string | null>(null);
  private trialEventId = '';
  private pageState = new Signal.Computed<PageState>(() => {
    if (this.loading.get()) return 'loading';
    if (this.errorMessage.get()) return 'error';
    return 'loaded';
  });

  static get observedAttributes(): string[] {
    return ['entry-id', 'trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
    }
  }

  connectedCallback(): void {
    this.updateRender();
    void this.loadEntry();
  }

  private async loadEntry(): Promise<void> {
    const entryId = this.getAttribute('entry-id');
    if (!entryId || !this.trialEventId) {
      this.loading.set(false);
      this.errorMessage.set('Entry ID or Trial Event ID is missing.');
      this.updateRender();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);
    this.supportId.set(null);
    this.updateRender();

    try {
      const response = await entriesApi.getById(this.trialEventId, entryId);

      if (response.error || !response.data) {
        this.entry.set(null);
        this.errorMessage.set(response.error?.title || 'Unable to load confirmation.');
        this.supportId.set(response.supportId || response.error?.traceId || null);
      } else {
        this.entry.set(response.data);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load confirmation.';
      this.entry.set(null);
      this.errorMessage.set(message);
    } finally {
      this.loading.set(false);
      this.updateRender();
    }
  }

  private renderLoading(): string {
    return `
      <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-12" data-testid="confirmation-loading">
        <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-500 dark:bg-success-500/10">
          <dt-spinner size="large"></dt-spinner>
        </div>
        <p class="text-sm font-medium text-neutral-600 dark:text-neutral-400">Loading confirmation...</p>
      </div>
    `;
  }

  private renderError(): string {
    const message = this.errorMessage.get() ?? 'Unable to load confirmation.';
    const supportId = this.supportId.get();

    return `
      <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-12 text-center" data-testid="confirmation-error">
        <div class="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error-50 text-error-500 dark:bg-error-500/10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
            <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Unable to load confirmation</h1>
        <p class="mb-2 text-sm text-neutral-600 dark:text-neutral-400">${this.esc(message)}</p>
        ${supportId ? `<p class="text-xs text-neutral-500 dark:text-neutral-400" data-testid="support-id">Support ID: ${this.esc(supportId!)}</p>` : ''}
      </div>
    `;
  }

  private renderSuccess(entry: EntryDetailDto): string {
    const isLoggedIn = authState.get().isAuthenticated;
    const entryNumber = entry.entryNumber ?? '—';
    const trialName = entry.trialEvent?.name ?? 'Trial event';
    const dogName = entry.dog.callName ?? '—';

    return `
      <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-12 text-center" data-testid="confirmation-success">
        <div class="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-50 text-success-500 dark:bg-success-500/10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
            <path fill-rule="evenodd" d="M2.25 12.75a.75.75 0 01.75-.75h1.542a.75.75 0 010 1.5H3.75v1.542a.75.75 0 01-1.5 0v-2.292zm18 0a.75.75 0 00-.75-.75h-1.542a.75.75 0 000 1.5h1.542v1.542a.75.75 0 001.5 0v-2.292zM6.53 11.47a.75.75 0 10-1.06 1.06l4.5 4.5a.75.75 0 001.06 0l8-8a.75.75 0 10-1.06-1.06L10.5 15.44l-3.97-3.97z" clip-rule="evenodd" />
          </svg>
        </div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Entry Submitted</h1>
        <p class="mb-6 text-sm text-neutral-600 dark:text-neutral-400">We'll email your confirmation shortly.</p>
        <div class="mb-6 w-full max-w-md rounded-2xl border border-success-200 bg-success-50 p-6 text-center dark:border-success-500/30 dark:bg-success-500/10">
          <div class="text-xs font-semibold uppercase tracking-[0.3em] text-success-600">Entry Number</div>
          <div class="mt-2 text-2xl font-bold text-neutral-900 dark:text-white" data-testid="entry-number">${this.esc(entryNumber)}</div>
          <div class="mt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-200" data-testid="trial-name">${this.esc(trialName)}</div>
          <div class="text-sm text-neutral-500 dark:text-neutral-400" data-testid="dog-name">${this.esc(dogName)}</div>
        </div>
        <div class="flex flex-wrap items-center justify-center gap-3">
          ${isLoggedIn ? `<button class="inline-flex items-center justify-center rounded-xl border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800" data-testid="view-entry-btn">View Entry Details</button>` : ''}
          <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600" data-testid="submit-another-btn">Submit Another Entry</button>
        </div>
      </div>
    `;
  }

  private updateRender(): void {
    const state = this.pageState.get();
    let content = '';

    if (state === 'loading') {
      content = this.renderLoading();
    } else if (state === 'error') {
      content = this.renderError();
    } else if (this.entry.get()) {
      content = this.renderSuccess(this.entry.get()!);
    }

    this.render(content);
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const viewBtn = this.$('[data-testid="view-entry-btn"]');
    if (viewBtn) {
      viewBtn.addEventListener('click', () => {
        const entry = this.entry.get();
        if (!entry) return;
        const trialEventId = entry.trialEvent?.trialEventId;
        if (!trialEventId) return;
        navigate(`/secretary/entries/${trialEventId}/${entry.entryId}`);
      });
    }

    const submitAnotherBtn = this.$('[data-testid="submit-another-btn"]');
    if (submitAnotherBtn) {
      submitAnotherBtn.addEventListener('click', () => {
        const entry = this.entry.get();
        const trialEventId = entry?.trialEvent?.trialEventId;
        navigate(trialEventId ? `/trials/${trialEventId}/register` : '/trials');
      });
    }
  }
}

customElements.define('dt-confirmation-page', ConfirmationPage);
